// invest_FE/src/components/verification/AuthenticatorReset.js
import React, { useState, useContext } from 'react';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { auth } from '../../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import './AuthenticatorReset.css';

const AuthenticatorReset = ({ userId, phoneNumber, onSuccess, onCancel }) => {
  const { send2faCode, disableAuthenticator } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [method, setMethod] = useState(null); // 'email' or 'sms'
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [code, setCode] = useState('');
  const [notification, setNotification] = useState('');
  const [isError, setIsError] = useState(false);

  const handleMethodSelect = async (selectedMethod) => {
    setNotification('');
    setIsError(false);
    setMethod(selectedMethod);
    setIsLoading(true);

    try {
      if (selectedMethod === 'email') {
        const response = await send2faCode({ userId, method: 'email' });
        setNotification(response.message);
      } else if (selectedMethod === 'sms') {
        const recaptchaContainer = document.getElementById('recaptcha-container-reset');
        if (recaptchaContainer) recaptchaContainer.innerHTML = '';
        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container-reset', { size: 'invisible' });
        const result = await signInWithPhoneNumber(auth, phoneNumber, verifier);
        setConfirmationResult(result);
        setNotification('A verification code has been sent to your phone.');
      }
    } catch (error) {
      addNotification(`Failed to send code: ${error.message}`, 'error');
      setMethod(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code) {
      setNotification('Please enter the verification code.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setNotification('');
    setIsError(false);

    try {
      let finalCode = code;
      if (method === 'sms') {
        if (!confirmationResult) throw new Error('SMS verification not initiated.');
        const credential = await confirmationResult.confirm(code);
        finalCode = await credential.user.getIdToken();
      }

      const response = await disableAuthenticator({ userId, method, code: finalCode });
      addNotification(response.message, 'success');
      onSuccess(); // Notify parent that reset was successful
    } catch (error) {
      setNotification(error.message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="authenticator-reset-container">
      <h4>Reset Authenticator</h4>
      <p>To reset your authenticator app, please first verify your identity using another method.</p>

      {notification && <div className={`notification ${isError ? 'error' : 'success'}`}>{notification}</div>}

      {!method ? (
        <div className="method-selection">
          <p>Please select a verification method:</p>
          <button onClick={() => handleMethodSelect('email')} className="btn" disabled={isLoading}>
            Send Code to Email
          </button>
          <button onClick={() => handleMethodSelect('sms')} className="btn" disabled={isLoading}>
            Send Code via SMS
          </button>
          <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
            Cancel
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="reset-form">
          <p>
            A code was sent to your <strong>{method}</strong>. Please enter it below.
          </p>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="_ _ _ _ _ _" maxLength="6" required autoFocus className="otp-input-field" disabled={isLoading} />
          <div className="form-actions">
            <button type="button" onClick={() => setMethod(null)} className="btn btn-secondary" disabled={isLoading}>
              Change Method
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify & Disable'}
            </button>
          </div>
        </form>
      )}
      <div id="recaptcha-container-reset"></div>
    </div>
  );
};

export default AuthenticatorReset;
